import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { SelectedDictionaryProvider } from "./context/selectedContext";
import { LanguageProvider, LANGUAGE_CHANGED_EVENT } from "./context/languageContext";
import { clearAuth, getToken, setUser, fetchUser } from "./auth/auth";
import { isLocal } from './utils/config';
import type { UserModel } from "./type/user";

import "./style/common.scss";
import "./App.scss";
import "./App-dark.scss";
import TopBar from "./pages/header/header";
import Login from "./pages/login/login";
import ProtectedRoute from "./pages/router/protectRoute";
import Flashcards from "./pages/flashcards/flashcards";
import Dictionaries from "./pages/dictionaries/dictionaries";
import Words from "./pages/words/words";
import UserSettings from "./pages/userSettings/userSettings";
import NotebookPage from "./pages/notebook/notebookPage";
import ConfirmEmail from "./pages/confirmEmail/confirmEmail";
import PrivacyPolicy from "./pages/privacyPolicy/privacyPolicy";
//import { UserParametersProvider } from "./context/userParameters/userParametersProvider";

const applyDarkMode = (user: UserModel | null) => {
  const isDark = user?.parameters?.dark_mode?.value === true;
  document.body.classList.toggle('dark-mode', isDark);
  document.body.classList.toggle("light-mode", !isDark);
};

const App: React.FC = () => {

  const location = useLocation();
  
  // Check if user is authenticated
  const isAuthenticated = !!getToken();
  
  // Don't show TopBar on login and register pages
  const publicRoutes = ["/login", "/register"];
  const showTopBar = isAuthenticated && !publicRoutes.includes(location.pathname);

  const nav = useNavigate();

  useEffect(() => {
    fetchUser().then((user) => {
      if (!user) {
        if (!isLocal && location.pathname !== "/confirm-email" && location.pathname !== "/privacy-policy") {
          clearAuth();
          nav("/login");
        }
        return;
      }
      setUser(user);
      applyDarkMode(user);
    });
  }, []);

  useEffect(() => {
    const handleSettingsUpdated = (e: Event) => {
      applyDarkMode((e as CustomEvent<UserModel>).detail);
    };
    document.addEventListener('user:settingsUpdated', handleSettingsUpdated);
    return () => document.removeEventListener('user:settingsUpdated', handleSettingsUpdated);
  }, []);

  useEffect(() => {
    // Language changed: the previously selected dictionary/words no longer apply.
    const handleLanguageChanged = () => {
      localStorage.removeItem("selected_dictionary");
      localStorage.removeItem("app_dictionaries");
      nav("/dictionaries");
    };
    document.addEventListener(LANGUAGE_CHANGED_EVENT, handleLanguageChanged);
    return () => document.removeEventListener(LANGUAGE_CHANGED_EVENT, handleLanguageChanged);
  }, [nav]);

  return (
    
    //<UserParametersProvider>
      <LanguageProvider>
      <SelectedDictionaryProvider>
        {showTopBar && <TopBar />}
        <Routes>
          <Route path="*" element={<div>Not found</div>} />
          <Route path="/" element={<Navigate to="/dictionaries" />} />
          <Route path="/login" element={<Login />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          <Route path="/flashcards" 
              element={<ProtectedRoute><Flashcards /></ProtectedRoute>} 
          />

          <Route path="/dictionaries"
            element={<ProtectedRoute><Dictionaries /></ProtectedRoute> }
          />
          
          <Route path="/words"
            element={<ProtectedRoute><Words /></ProtectedRoute>}
          />

          <Route path="/settings"
            element={<ProtectedRoute><UserSettings /></ProtectedRoute>}              
          />

          <Route path="/notebook"
            element={<ProtectedRoute><NotebookPage /></ProtectedRoute>}             
          />

        </Routes>
      </SelectedDictionaryProvider>
      </LanguageProvider>
    //</UserParametersProvider>
  );
};

export default App;